/**
 * Inline "action denied" affordance detection (issue #248, block-message UX).
 *
 * The sibling of the page-view denial surface (`PermissionDeniedPage`): when an
 * agent is blocked from *triggering an API / action* by RBAC, the BFF's
 * `requirePermission` guard refuses the call with **HTTP 403** and the JSON body
 * `{ error: 'forbidden', action, state }` (see `server/src/rbac/middleware.ts`).
 * Per @dylan219's authoritative block-message copy (2026-07-03), that specific
 * case must surface the VERBATIM message below — inline, beside the action
 * button — never a raw 403 or a generic error.
 *
 * This is distinct from:
 *   - the page-view denial (`PermissionDeniedPage` — "You do not have permission
 *     to view this page");
 *   - the inline gateway-error affordance (#80/#82/#94 — the gateway's own
 *     Code + Message beside the button, a business decline, not an RBAC denial);
 *   - the Teller-ID gate, which refuses with `403 { error: 'invalid_teller_id' }`
 *     (a DIFFERENT 403 body — this helper deliberately does NOT match it, so an
 *     invalid Teller ID keeps its own distinct message).
 *
 * This is a UX affordance ONLY. The authoritative enforcement stays the RBAC
 * engine + the server route guards; the 403 is never relaxed. Because the signal
 * is the server 403 body (not any client-derived state), the message can only
 * appear when the server actually refused the action.
 */

/** The authoritative verbatim copy for an RBAC action-denial (@dylan219). */
export const ACTION_DENIED_MESSAGE =
  'Sorry, you do not have permission to perform this action.';

/**
 * #727 — authoritative verbatim copy for the "online-only action refused only
 * because the state resolved Offline" case (the #628 `onlineWouldAllow` signal).
 * This is NOT a role-level permission gap, so it must NOT use
 * `ACTION_DENIED_MESSAGE`. @dylan219 (2026-08-27): the message must clearly tell
 * the agent they are in offline status and must be online with the customer.
 */
export const CONNECTIVITY_OR_OFFLINE_MESSAGE =
  'you are currently in an "offline status". To proceed with completing your ' +
  'request you must be in an "online Status" with the customer on the phone ' +
  'and security passed.';

/**
 * #805 — authoritative verbatim copy for the offline denial on the
 * **manual internet password** creation action (SCRN-22 "Create manual
 * password" → `POST /security/internet-password`). The write is ONLINE-ONLY
 * (#734), so an agent whose role carries the grant but whose session resolves
 * Offline is refused with `403 { error: 'forbidden', onlineWouldAllow: true }`
 * — they hold the permission and only lack the live call. @dylan219
 * (2026-09-16) set this exact copy for THIS surface (capitalisation +
 * single-quote style included). Scoped to this action only: the generic #248
 * `ACTION_DENIED_MESSAGE` and the shared #727 `CONNECTIVITY_OR_OFFLINE_MESSAGE`
 * stay untouched everywhere else.
 */
export const OFFLINE_IPW_DENY_MESSAGE =
  "You are currently in an 'Offline Status'. To proceed with completing your " +
  "request you must be in an 'Online Status' with the customer on the phone " +
  'and security passed.';

/**
 * #763 (CCF-54) — authoritative verbatim copy for the "GENUINE 2FA required"
 * refusal on the two bill-payment write actions (create bill payment / transfer
 * to payee). The BFF's `requireOtp2fa` guard refuses with HTTP 403
 * `{ error: 'two_factor_required' }` when the bound customer has no real OTP
 * validation (reaching `medium` via additional questions / high security does
 * NOT satisfy CCF-54). Surfaced inline beside the action button.
 */
export const TWO_FACTOR_REQUIRED_MESSAGE =
  '2-factor authentication (OTP) must be completed before you can create a ' +
  'bill payment or transfer to a payee. Additional questions or high ' +
  'security cannot satisfy this requirement — please complete 2FA and try ' +
  'again.';

/**
 * #763 — True when a 403 is the CCF-54 genuine-2FA refusal — i.e. an HTTP 403
 * whose JSON body is `{ error: 'two_factor_required' }` (the shape
 * `requireOtp2fa` returns). Distinct from the RBAC `forbidden` denial and the
 * Teller-ID `invalid_teller_id` refusal.
 */
export function isTwoFactorRequired(
  status: number | undefined,
  errorBody: unknown,
): boolean {
  if (status !== 403) return false;
  const body = (errorBody ?? {}) as { error?: unknown };
  return body.error === 'two_factor_required';
}

/**
 * #788 (CCF-16) — authoritative inline copy for a terminal OTP lockout surfaced
 * on Send Code (the customer was blocked by 3 failed attempts in 24h and has
 * been re-identified). Matches the dedicated `otpBlocked` alert copy on the EDS
 * medium screen; used where a screen has no separate blocked-state alert
 * (registration) and shows the lockout inline via `otpError`.
 */
export const OTP_LOCKED_OUT_MESSAGE =
  'The one-time passcode was entered incorrectly 3 times. OTP generation is ' +
  'blocked for 24 hours — advise the customer to visit a branch and add a ' +
  'customer note.';

/**
 * #788 (CCF-16) — True when a 403 is the terminal OTP-lockout refusal: an HTTP
 * 403 whose JSON body is `{ error: 'otp_locked_out' }` (the shape the
 * `otp/generate` route returns when a sticky, per-CIF 24h lockout is in force —
 * #639). This is NOT an RBAC/teller-ID denial, so it must NOT surface the
 * generic "check Teller ID and permissions" copy. Send Code handlers use this to
 * route the lockout to their dedicated inline CCF-16 block alert instead — the
 * lockout itself stays enforced server-side; only the message + placement change.
 */
export function isOtpLockedOut(
  status: number | undefined,
  errorBody: unknown,
): boolean {
  if (status !== 403) return false;
  const body = (errorBody ?? {}) as { error?: unknown };
  return body.error === 'otp_locked_out';
}

/**
 * True when a BFF response is an RBAC *action* denial — i.e. an HTTP 403 whose
 * JSON body is `{ error: 'forbidden' }` (the shape `requirePermission` returns).
 *
 * `errorBody` is the parsed error body openapi-fetch returns in its `error`
 * field on a non-2xx response; `status` is `response.status`. Both are required
 * so a 403 from a different guard (e.g. `invalid_teller_id`) is NOT matched.
 */
export function isActionForbidden(
  status: number | undefined,
  errorBody: unknown,
): boolean {
  if (status !== 403) return false;
  const body = (errorBody ?? {}) as { error?: unknown };
  return body.error === 'forbidden';
}

/**
 * #611 — True when a 403 forbidden is an ONLINE-ONLY action refused ONLY because
 * the state resolved Offline (the #628 signal `onlineWouldAllow: true` on the
 * 403 body). This distinguishes "no live Genesys call / dropped link / offline
 * journey" from a genuine role-level RBAC denial, so the inline transfer error
 * can show the connectivity/offline message instead of the permission copy.
 * `isActionForbidden` must be true first (it is checked here), so a non-forbidden
 * 403 is never matched.
 */
export function isOnlineOnlyOfflineDenial(
  status: number | undefined,
  errorBody: unknown,
): boolean {
  if (!isActionForbidden(status, errorBody)) return false;
  const body = (errorBody ?? {}) as { onlineWouldAllow?: unknown };
  return body.onlineWouldAllow === true;
}

/**
 * #727 — resolves the appropriate inline block message for a BFF 403.
 * Checks the offline-state denial first (`onlineWouldAllow`), then the
 * role-level RBAC denial, returning `null` for any other 403 or non-403.
 *
 * Use this in place of the raw `isActionForbidden` / `isOnlineOnlyOfflineDenial`
 * pair in every action screen so that an offline-only denial gets the correct
 * @dylan219 offline-status copy instead of the generic permission message.
 */
export function resolveActionBlockMessage(
  status: number | undefined,
  errorBody: unknown,
): string | null {
  // #763 (CCF-54) — the genuine-2FA refusal on the two bill-payment write
  // actions. Checked first: it is a distinct 403 body from the RBAC/offline
  // denials, and its copy tells the agent to complete 2FA (not a role gap).
  if (isTwoFactorRequired(status, errorBody))
    return TWO_FACTOR_REQUIRED_MESSAGE;
  if (isOnlineOnlyOfflineDenial(status, errorBody))
    return CONNECTIVITY_OR_OFFLINE_MESSAGE;
  if (isActionForbidden(status, errorBody)) return ACTION_DENIED_MESSAGE;
  return null;
}
